import { useEffect, useRef, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  familiasDoCatalogo, modelosBase, montadorasCatalogo, veiculosDoCliente,
} from '../lib/consultas';
import {
  CARRO_VAZIO, MONTADORAS_DOS_BOTOES, carroDoTexto, carroPreenchido, carrosNoTexto,
  comFipe, comMontadora, entenderCarro, escolherSugestao, nomeDaMontadora, palavrasParaFipe,
  sugestoesDeCarro, textoDoCampo, textoDoCarro, type BaseDeCarros, type CarroEscolhido,
} from '../lib/carro';

// O carro da VENDA (migração 073), num CAMPO SÓ desde 08/10/2026.
//
// De 11/09 a 08/10 eram duas listas, montadora e modelo, "para o colaborador
// colocar o nome do carro corretamente" (pedido do dono). O modelo era a
// VERSÃO da FIPE: 549 opções na VW, "Gol (novo) 1.0 Mi Total Flex 8V 4p". O
// balcão desistiu da lista — 5,7% das vendas com carro — e continuou
// escrevendo o carro no texto da peça diversa ("LÂMPADA H4 GOL").
//
// Agora o vendedor digita como fala e a tela lê (lib/carro.ts). O que o dono
// pediu em 11/09 continua de pé, por outro caminho: a montadora nunca é texto
// livre (vem do modelo reconhecido, de uma palavra que É montadora, ou de um
// botão) e sai sempre na grafia da FIPE; o modelo sai com o nome-base na
// frente ("Gol 1.0", "Uno Mille"). E a linha de leitura embaixo mostra,
// antes de vender, exatamente o que vai ser gravado.
//
// A TRAVA DE "TOCADO" (CLAUDE.md). A lista de modelos e as famílias da FIPE
// chegam DEPOIS de o vendedor digitar. O texto é dele e nunca é reescrito; o
// que chega tarde só melhora a leitura. E a releitura só é empurrada depois
// de um toque NESTE campo: abrir a correção de uma venda de setembro não
// reescreve a versão FIPE que ela guardou (ver CarroEscolhido.texto).
//
// SEM <datalist>: no Safari do iPhone ele mal aparece. As sugestões são
// botões de 44 px.
//
// POR QUE NÃO É O MESMO CAMPO DO ORÇAMENTO. `quotes.veiculo` é FRASE LIVRE por
// decisão da 049 — é o que se ouve ao telefone. Aqui o texto é LIDO e vai para
// colunas separadas que o relatório agrupa (modelo_base_de, 092).

export type { CarroEscolhido } from '../lib/carro';
export { CARRO_VAZIO, carroPreenchido, nomeDaMontadora, textoDoCarro } from '../lib/carro';

/** O alvo de toque de todo botão daqui: 44 px de altura. */
const BOTAO = 'min-h-11 border border-edge px-3 text-sm hover:border-signal';

/** Os botões de "de que montadora?" pelo nome que o balcão fala. */
const ROTULO_DA_MONTADORA: Record<string, string> = {
  'VW - VolksWagen': 'VW',
  'GM - Chevrolet': 'Chevrolet',
};

export default function SeletorVeiculo({
  valor, aoTrocar, clienteId = null, idPrefixo = 'carro', rotulo = 'Carro',
  textos = [], linha = false,
}: {
  valor: CarroEscolhido;
  aoTrocar: (c: CarroEscolhido) => void;
  /** Cliente CADASTRADO: havendo frota (051), ela aparece antes do campo. */
  clienteId?: string | null;
  idPrefixo?: string;
  rotulo?: string;
  /** O texto das peças (a descrição da peça diversa). O carro citado ali
   *  vira botão com o campo vazio — e NUNCA preenche sozinho. */
  textos?: ReadonlyArray<string | null | undefined>;
  /** O carro de UMA linha da venda ("esta peça é de outro carro"). sale_items
   *  não tem ano, então o número fica no modelo; e meio carro ali trava a
   *  venda (a RPC recusa), em vez de simplesmente não ir. */
  linha?: boolean;
}) {
  const opcoes = { comAno: !linha };

  const frota = useQuery({
    queryKey: ['veiculos', clienteId],
    queryFn: () => veiculosDoCliente(clienteId!),
    enabled: Boolean(clienteId),
  });
  // 45 linhas, que só mudam quando o dono cadastra um modelo.
  const modelos = useQuery({
    queryKey: ['modelos-base'],
    queryFn: modelosBase,
    staleTime: Infinity,
  });
  // A mesma consulta (e a mesma chave) do autocomplete do Cadastro.
  const montadoras = useQuery({
    queryKey: ['catalogo', 'montadoras'],
    queryFn: montadorasCatalogo,
    staleTime: 24 * 60 * 60_000,
  });

  const texto = textoDoCampo(valor);
  const vazio = texto.trim() === '';
  const baseSemFipe: BaseDeCarros = {
    modelos: modelos.data ?? [],
    montadoras: montadoras.data ?? [],
    familias: [],
  };

  // As palavras que podem ser o modelo e vehicle_models não conhece vão às
  // famílias da FIPE ("fusca", "kombi", "147"; em "pampa pick up", a pampa,
  // antes de o "up" ganhar) — todas numa ida só (palavrasParaFipe), com
  // 250 ms de espera, para não perguntar ao banco a cada letra.
  const palavras = palavrasParaFipe(texto, baseSemFipe, opcoes).join(' ');
  const [palavrasAtrasadas, setPalavrasAtrasadas] = useState(palavras);
  useEffect(() => {
    if (palavras === palavrasAtrasadas) return undefined;
    const t = setTimeout(() => setPalavrasAtrasadas(palavras), 250);
    return () => clearTimeout(t);
  }, [palavras, palavrasAtrasadas]);
  // Carro que veio pronto (venda antiga, sem `texto`) não é relido — então
  // também não vai à FIPE só por abrir a correção.
  const consultaFipe = palavrasAtrasadas !== '' && valor.texto !== undefined;
  const familias = useQuery({
    queryKey: ['familias-fipe', palavrasAtrasadas],
    queryFn: () => familiasDoCatalogo(palavrasAtrasadas.split(' ')),
    enabled: consultaFipe,
    staleTime: 60 * 60_000,
    // Enquanto "fusca" não volta, a resposta de "fusc" ainda serve: o que
    // vale é o PREFIXO respondido (BaseDeCarros.fipe), e a resposta de "fusc"
    // cobre "fusca". A de "2008" não cobre "griffe" — essa espera.
    placeholderData: keepPreviousData,
  });
  const base = comFipe(baseSemFipe, familias.data);
  // Ainda chegando dado que pode mudar a leitura: nada de perguntar "de que
  // montadora?" para um "fusca" que a FIPE vai reconhecer daqui a pouco.
  const aguardando = modelos.isPending || palavras !== palavrasAtrasadas
    || (consultaFipe && familias.isFetching);

  const leitura = entenderCarro(texto, base, opcoes);
  const relido: CarroEscolhido = {
    ...valor,
    montadora: leitura.montadora ?? '',
    modelo: leitura.modelo ?? '',
    ano: opcoes.comAno && leitura.ano ? String(leitura.ano) : '',
  };

  const tocado = useRef(false);
  const ultimoEmpurrado = useRef('');
  const escrever = (novo: CarroEscolhido) => {
    tocado.current = true;
    aoTrocar(novo);
  };
  const digitar = (t: string) => escrever(carroDoTexto(t, base, valor, opcoes));

  // O dado que chega DEPOIS do toque (a família de "fusca", a lista de
  // modelos na primeira abertura) completa a leitura do que já foi digitado.
  // Só depois de um toque neste campo, só com o dado assentado, e só o que
  // mudou — o texto, nunca.
  const diverge = relido.montadora !== valor.montadora || relido.modelo !== valor.modelo
    || relido.ano !== valor.ano;
  useEffect(() => {
    if (!tocado.current || aguardando || !diverge || valor.texto === undefined) return;
    const assinatura = [valor.texto, relido.montadora, relido.modelo, relido.ano].join('|');
    if (ultimoEmpurrado.current === assinatura) return;
    ultimoEmpurrado.current = assinatura;
    aoTrocar(relido);
  });

  const carros = frota.data ?? [];
  const doTexto = vazio ? carrosNoTexto(textos, base) : [];
  const sugestoes = !vazio && leitura.fonte === 'livre' ? sugestoesDeCarro(texto, base) : [];
  const semMontadora = !aguardando && valor.modelo.trim() !== '' && valor.montadora.trim() === '';
  const semModelo = valor.montadora.trim() !== '' && valor.modelo.trim() === '';
  // Palavra ainda pela metade ("pal") tem sugestão: perguntar a montadora ao
  // mesmo tempo seria ruído. A pergunta inteira fica para o que não tem.
  const perguntarMontadora = semMontadora && sugestoes.length === 0;
  const avisoSemMontadora = linha
    ? 'Sem a montadora, a venda não fecha com este carro — escolha uma, ou use o carro da venda.'
    : 'Sem a montadora, o carro não vai para a venda.';

  return (
    <div>
      {rotulo && <label className="rotulo" htmlFor={`${idPrefixo}-texto`}>{rotulo}</label>}

      {/* A frota do cliente vem primeiro: quando o carro já está cadastrado,
          escolher dali é um toque e ainda AMARRA a venda ao carro
          (customer_vehicle_id), que é o que responde "o que este carro já
          comprou". O cadastro é texto livre (051): ele entra no campo e passa
          pela mesma leitura de quem digita. */}
      {carros.length > 0 && (
        <select
          className="campo mb-2 [color-scheme:dark]"
          aria-label="Carro cadastrado do cliente"
          value={valor.veiculoId ?? ''}
          onChange={(e) => {
            const v = carros.find((x) => x.id === e.target.value);
            if (!v) { escrever({ ...valor, veiculoId: null }); return; }
            const t = [v.montadora, v.modelo, v.ano ? String(v.ano) : null].filter(Boolean).join(' ');
            escrever({ ...(t ? carroDoTexto(t, base, valor, opcoes) : valor), veiculoId: v.id });
          }}
        >
          <option value="">Carro do cliente…</option>
          {carros.map((v) => (
            <option key={v.id} value={v.id}>
              {v.apelido || [v.montadora, v.modelo].filter(Boolean).join(' ') || v.placa}
              {v.placa ? ` (${v.placa})` : ''}
            </option>
          ))}
        </select>
      )}

      <div className="flex gap-2">
        <input
          id={`${idPrefixo}-texto`}
          className="campo"
          type="text"
          inputMode="text"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="none"
          spellCheck={false}
          enterKeyHint="done"
          placeholder={linha ? 'ex.: onix 1.0' : 'ex.: gol 2010 1.0'}
          aria-label={rotulo ? undefined : 'Carro'}
          value={texto}
          onChange={(e) => digitar(e.target.value)}
        />
        {!vazio && (
          <button
            type="button"
            className="min-h-11 min-w-11 shrink-0 border border-edge text-lg text-mute hover:border-signal"
            aria-label="Limpar o carro"
            onClick={() => escrever({ ...CARRO_VAZIO })}
          >
            ×
          </button>
        )}
      </div>

      {/* O que vai ser gravado — e não o que se acha que vai. */}
      {carroPreenchido(valor) && (
        <p className="mt-1 text-sm" aria-live="polite">
          <span className="text-dim">Vai gravado: </span>
          <strong>{textoDoCarro(valor, ' · ')}</strong>
        </p>
      )}

      {sugestoes.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2" role="group" aria-label="Sugestões de carro">
          {sugestoes.map((s) => (
            <button
              key={`${s.montadora}|${s.nome}`}
              type="button"
              className={BOTAO}
              onClick={() => digitar(escolherSugestao(texto, base, s, opcoes))}
            >
              <span className="font-semibold">{s.nome}</span>
              <span className="text-dim"> · {nomeDaMontadora(s.montadora)}</span>
            </button>
          ))}
        </div>
      )}
      {semMontadora && !perguntarMontadora && (
        <p className="mt-1 text-xs text-mute">{avisoSemMontadora}</p>
      )}

      {doTexto.length > 0 && (
        <div className="mt-2">
          <p className="text-xs text-mute">Do texto das peças:</p>
          <div className="mt-1 flex flex-wrap gap-2">
            {doTexto.map((m) => (
              <button
                key={`${m.montadora}|${m.nome}`}
                type="button"
                className={BOTAO}
                onClick={() => digitar(escolherSugestao('', base, m, opcoes))}
              >
                {m.nome}
              </button>
            ))}
          </div>
        </div>
      )}

      {perguntarMontadora && (
        <div className="mt-2 border border-alerta/40 bg-alerta/5 p-2">
          <p className="text-sm">Não achei esse carro — de que montadora?</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {MONTADORAS_DOS_BOTOES.map((m) => (
              <button
                key={m}
                type="button"
                className={BOTAO}
                onClick={() => digitar(comMontadora(texto, m))}
              >
                {ROTULO_DA_MONTADORA[m] ?? nomeDaMontadora(m)}
              </button>
            ))}
            {/* Sem text-sm: fonte menor que 16 px faz o iPhone dar zoom. O
                color-scheme escuro faz o WebKit desenhar o controle nativo
                escuro: sem ele, saía cinza-claro com letra branca, ilegível
                (medido no motor do Safari em 08/10/2026). */}
            <select
              className="campo min-h-11 w-auto py-2 [color-scheme:dark]"
              aria-label="Outra montadora"
              value=""
              onChange={(e) => { if (e.target.value) digitar(comMontadora(texto, e.target.value)); }}
            >
              <option value="">outra…</option>
              {(montadoras.data ?? []).map((m) => (
                <option key={m} value={m}>{nomeDaMontadora(m)}</option>
              ))}
            </select>
          </div>
          <p className="mt-1 text-xs text-mute">{avisoSemMontadora}</p>
        </div>
      )}

      {semModelo && (
        <p className="mt-1 text-xs text-mute">
          Falta o modelo — o sistema não guarda meio carro.
        </p>
      )}
    </div>
  );
}
